import { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Tooltip,
  Autocomplete,
  Grid,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import type { CriteriaFormRowProps } from './types';
import type { FieldDefinition, OperatorType, FieldOption } from '../../schemas/types';
import { getSchemaForEntityType, OPERATOR_LABELS } from '../../schemas';
import { getFieldByPath } from './utils';

export const CriteriaFormRow = ({
  condition,
  entityType,
  onUpdate,
  onRemove,
  disabled = false,
}: CriteriaFormRowProps) => {
  const schema = getSchemaForEntityType(entityType);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedField, setSelectedField] = useState<FieldDefinition | null>(null);

  // Initialize category and field from condition path
  useEffect(() => {
    if (!schema || !condition.path) return;

    for (const cat of schema.categories) {
      const field = cat.fields.find((f) => f.path === condition.path);
      if (field) {
        setSelectedCategory(cat.name);
        setSelectedField(field);
        break;
      }
    }
  }, [schema, condition.path]);

  const handleCategoryChange = (categoryName: string) => {
    setSelectedCategory(categoryName);
    const category = schema?.categories.find((c) => c.name === categoryName);
    if (category && category.fields.length > 0) {
      const field = category.fields[0];
      setSelectedField(field);
      onUpdate({
        ...condition,
        path: field.path,
        operator: field.operators[0],
        value: '',
      });
    }
  };

  const handleFieldChange = (fieldPath: string) => {
    const field = getFieldByPath(entityType, fieldPath);
    if (field) {
      setSelectedField(field);
      onUpdate({
        ...condition,
        path: field.path,
        operator: field.operators[0],
        value: '',
      });
    }
  };

  const handleOperatorChange = (operator: OperatorType) => {
    let newValue = condition.value;

    // Reset value if operator type changes significantly
    if (operator === 'between' && !Array.isArray(condition.value)) {
      newValue = [0, 0];
    } else if (operator !== 'between' && Array.isArray(condition.value)) {
      newValue = '';
    }

    onUpdate({
      ...condition,
      operator,
      value: newValue,
    });
  };

  const handleValueChange = (value: string | number | string[] | [number, number]) => {
    onUpdate({
      ...condition,
      value,
    });
  };

  const currentCategory = schema?.categories.find((c) => c.name === selectedCategory);
  const fieldsInCategory = currentCategory?.fields || [];

  const renderValueInput = () => {
    if (!selectedField) return null;

    const { type, options, placeholder } = selectedField;
    const { operator, value } = condition;

    // Between operator - show two inputs
    if (operator === 'between') {
      const betweenValue = Array.isArray(value) ? value : [0, 0];
      return (
        <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
          <TextField
            size="small"
            type="number"
            label="From"
            value={betweenValue[0] || ''}
            onChange={(e) => handleValueChange([Number(e.target.value), betweenValue[1] as number])}
            disabled={disabled}
            sx={{ flex: 1 }}
          />
          <TextField
            size="small"
            type="number"
            label="To"
            value={betweenValue[1] || ''}
            onChange={(e) => handleValueChange([betweenValue[0] as number, Number(e.target.value)])}
            disabled={disabled}
            sx={{ flex: 1 }}
          />
        </Box>
      );
    }

    // Array type with options - autocomplete
    if (type === 'array' && options && options !== 'dynamic') {
      const fieldOptions = options as FieldOption[];
      return (
        <Autocomplete
          size="small"
          options={fieldOptions}
          getOptionLabel={(option) => option.label}
          value={fieldOptions.find((o) => o.value === value) || null}
          onChange={(_, newValue) => handleValueChange(newValue?.value || '')}
          disabled={disabled}
          renderInput={(params) => (
            <TextField {...params} label="Value" placeholder={placeholder} />
          )}
          sx={{ flex: 1, minWidth: 200 }}
        />
      );
    }

    // String type with options - select
    if (type === 'string' && options && options !== 'dynamic') {
      const fieldOptions = options as FieldOption[];
      return (
        <FormControl size="small" sx={{ flex: 1, minWidth: 150 }}>
          <InputLabel>Value</InputLabel>
          <Select
            value={value || ''}
            onChange={(e) => handleValueChange(e.target.value)}
            label="Value"
            disabled={disabled}
          >
            {fieldOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    // Dynamic options - text input with autocomplete hint
    if (options === 'dynamic') {
      return (
        <TextField
          size="small"
          label="Value"
          placeholder={placeholder || 'Enter value...'}
          value={value || ''}
          onChange={(e) => handleValueChange(e.target.value)}
          disabled={disabled}
          sx={{ flex: 1, minWidth: 200 }}
          helperText="Enter exact value"
        />
      );
    }

    // Number type
    if (type === 'number') {
      return (
        <TextField
          size="small"
          type="number"
          label="Value"
          placeholder={placeholder}
          value={value || ''}
          onChange={(e) => handleValueChange(Number(e.target.value))}
          disabled={disabled}
          sx={{ flex: 1, minWidth: 120 }}
        />
      );
    }

    // Date type
    if (type === 'date') {
      return (
        <TextField
          size="small"
          type="date"
          label="Value"
          value={value || ''}
          onChange={(e) => handleValueChange(e.target.value)}
          disabled={disabled}
          InputLabelProps={{ shrink: true }}
          sx={{ flex: 1, minWidth: 150 }}
        />
      );
    }

    // Default text input
    return (
      <TextField
        size="small"
        label="Value"
        placeholder={placeholder}
        value={value || ''}
        onChange={(e) => handleValueChange(e.target.value)}
        disabled={disabled}
        sx={{ flex: 1, minWidth: 150 }}
      />
    );
  };

  if (!schema) {
    return (
      <Box sx={{ p: 2 }}>
        <TextField
          size="small"
          fullWidth
          label="No schema available"
          disabled
          helperText={`Select a valid entity type`}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1 }}>
      <Grid container spacing={1} sx={{ flex: 1 }}>
        {/* Category Select */}
        <Grid item xs={12} sm={3}>
          <FormControl size="small" fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              value={selectedCategory}
              onChange={(e) => handleCategoryChange(e.target.value)}
              label="Category"
              disabled={disabled}
            >
              {schema.categories.map((cat) => (
                <MenuItem key={cat.name} value={cat.name}>
                  {cat.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Field Select */}
        <Grid item xs={12} sm={3}>
          <FormControl size="small" fullWidth>
            <InputLabel>Field</InputLabel>
            <Select
              value={condition.path}
              onChange={(e) => handleFieldChange(e.target.value)}
              label="Field"
              disabled={disabled || !selectedCategory}
            >
              {fieldsInCategory.map((field) => (
                <MenuItem key={field.path} value={field.path}>
                  {field.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Operator Select */}
        <Grid item xs={12} sm={2}>
          <FormControl size="small" fullWidth>
            <InputLabel>Operator</InputLabel>
            <Select
              value={condition.operator}
              onChange={(e) => handleOperatorChange(e.target.value as OperatorType)}
              label="Operator"
              disabled={disabled || !selectedField}
            >
              {selectedField?.operators.map((op) => (
                <MenuItem key={op} value={op}>
                  {OPERATOR_LABELS[op]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Value Input */}
        <Grid item xs={12} sm={4}>
          {renderValueInput()}
        </Grid>
      </Grid>

      {/* Remove Button */}
      <Tooltip title="Remove condition">
        <IconButton
          size="small"
          onClick={onRemove}
          disabled={disabled}
          sx={{ mt: 0.5 }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};
